import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

interface LegalPageProps {
  type: 'privacy' | 'terms';
}

const Legal: React.FC<LegalPageProps> = ({ type }) => {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        {/* A full page load: the landing page is the static public/ezschool/ezrph/ (#90). */}
        <a href="/ezschool/ezrph/" className="legal-logo">
          <img src="/logo.png" alt="Ez-RPH" />
          <span>Ez-RPH</span>
        </a>
      </nav>
      
      <div className="legal-container">
        {type === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
      </div>

      <footer className="legal-footer">
        <p>© {new Date().getFullYear()} Gatal Tech Enterprise. All rights reserved.</p>
        <div className="legal-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
};

const PrivacyPolicy: React.FC = () => (
  <article className="legal-content">
    <h1>Privacy Policy</h1>
    <p className="legal-updated">Last updated: 28 October 2026</p>

    <p>
      Gatal Tech Enterprise ("we," "us," or "our") operates Ez-RPH, including the Ez-RPH web and
      mobile apps, school features, website, and companion browser tools such as the SPPB PBD
      Helper Chrome extension. This Privacy Policy explains how we collect, use, disclose, and
      safeguard your information when you use Ez-RPH.
    </p>

    <h2>1. Information We Collect</h2>
    
    <h3>Personal Information</h3>
    <ul>
      <li>Name and display name</li>
      <li>Email address</li>
      <li>Phone number (required on the Ez-RPH website and the contact form; optional in the iPhone and Android apps)</li>
      <li>School/organization information</li>
      <li>Student roster records entered by authorized teachers or schools, including class assignments and optional sensitive fields</li>
      <li>Ez-RPH account authentication information, including Firebase authentication tokens needed to keep you signed in</li>
      <li>Profile picture (optional)</li>
    </ul>

    <h3>Usage Information</h3>
    <ul>
      <li>Lesson plans and teaching content you create</li>
      <li>App usage patterns and preferences</li>
      <li>Device information (model, operating system)</li>
      <li>Log data and error reports</li>
    </ul>

    <h3>SPPB PBD Helper Extension Information</h3>
    <p>
      If you install and use the SPPB PBD Helper Chrome extension, Ez-RPH may read and process
      information from the SPPB PBD pages where you run the helper. This may include SPPB subject
      rows, class and subject names, student names, identification numbers, TP values, Kemahiran
      values, Ulasan Guru, Intervensi, Usaha Murid, row status, subject mappings, scan/compare
      results, fill job status, and debug summaries needed to verify whether a save worked.
    </p>

    <h2>2. How We Use Your Information</h2>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Provide, maintain, and improve Ez-RPH services</li>
      <li>Process your account registration and manage your subscription</li>
      <li>Sync your lesson plans across devices</li>
      <li>Enable school organization features and approval workflows</li>
      <li>Compare Ez-RPH PBD records with your active SPPB PBD page when you choose to run the SPPB PBD Helper</li>
      <li>Fill matched SPPB PBD fields after your action and save them using SPPB's Simpan flow</li>
      <li>Record SPPB sync status back into Ez-RPH so you can see whether a subject was saved</li>
      <li>Send important notifications about your account or the service</li>
      <li>Know who is using Ez-RPH and its SPPB helper, and contact you about your account</li>
      <li>With your separate consent, send you Ez-RPH tips and offers on WhatsApp. You can tell us to stop at any time.</li>
      <li>Respond to your inquiries and provide customer support</li>
      <li>Analyze usage patterns to improve user experience</li>
      <li>Comply with legal obligations</li>
    </ul>

    <h2>3. Data Storage and Security</h2>
    <ul>
      <li>Your data is stored securely using Firebase (Google Cloud) infrastructure</li>
      <li>We implement industry-standard security measures including encryption</li>
      <li>Local data is stored on your device using secure storage methods</li>
      <li>The SPPB PBD Helper stores extension state in Chrome storage, including Ez-RPH Firebase tokens, token expiry, user id, email, latest scan/compare/fill results, debug summaries, subject mappings, and fill progress</li>
      <li>The SPPB PBD Helper does not store IDME or SPPB passwords and does not click or submit SPPB Hantar</li>
      <li>We regularly review and update our security practices</li>
    </ul>

    <h2>4. Data Sharing</h2>
    <p>We do not sell your personal information. We may share your data only:</p>
    <ul>
      <li>With your school organization (if you join one), including lesson plans, teacher profile data, class data, and school-managed student records where permitted by role and access grants</li>
      <li>With assigned teachers for school-managed student records that they need for class and subject duties</li>
      <li>With service providers who assist in operating our application (e.g., Firebase)</li>
      <li>With Ez-RPH Firebase callables when the SPPB PBD Helper requests matching PBD payloads or writes sync status</li>
      <li>With the active SPPB pages in your own browser session when you choose to fill and save matched PBD fields</li>
      <li>When required by Malaysian law or legal process</li>
      <li>To protect our rights, privacy, safety, or property</li>
    </ul>

    <h2>5. Your Rights</h2>
    <p>Under the Malaysian Personal Data Protection Act 2010 (PDPA), you have the right to:</p>
    <ul>
      <li>Access your personal data</li>
      <li>Correct inaccurate personal data</li>
      <li>Withdraw consent for data processing</li>
      <li>Request deletion of your account and data</li>
      <li>Export your lesson plan data</li>
    </ul>

    <h2>6. Data Retention</h2>
    <ul>
      <li>We retain your data for as long as your account is active</li>
      <li>Lesson plans older than one year may be archived</li>
      <li>SPPB PBD Helper local extension data remains in Chrome storage until it is cleared, the extension is removed, or the browser clears extension storage</li>
      <li>SPPB sync status stored in Ez-RPH is retained as part of your account and school records while those records are active</li>
      <li>Upon account deletion, your data will be removed within 30 days</li>
      <li>Some data may be retained for legal compliance purposes</li>
    </ul>

    <h2>7. Student and Minor Data</h2>
    <p>
      Ez-RPH is designed for teachers and schools. When the student registry is used, the service intentionally
      stores student records that may relate to minors, including sensitive fields such as race, religion, identity
      number, birth date, OKU/disability information, and orphan status where an authorized user enters them.
      Users and schools must have lawful authority to provide this data. We process student data to provide school
      roster, class assignment, support, security, backup, compliance, billing/admin, and service-improvement
      functions. Student-identifiable data is not sold and is not used for AI model training unless a separate
      explicit permission and policy says so.
    </p>

    <h2>8. SPPB PBD Helper Chrome Extension</h2>
    <p>
      The SPPB PBD Helper is a companion tool for Ez-RPH users. Its single purpose is to connect
      a logged-in Ez-RPH account with the teacher's active SPPB session, compare PBD rows, fill
      matched fields after user action, and save using SPPB Simpan. It is not represented as an
      official Ministry of Education or SPPB product.
    </p>
    <p>
      The extension does not run on all websites. It is limited to Ez-RPH web pages, SPPB/IDME
      workflow pages, Firebase authentication endpoints, and Ez-RPH backend endpoints required
      for this feature. It does not sell data, use data for advertising, or use student-identifiable
      data for AI model training.
    </p>

    <h2>9. Chrome Web Store Limited Use</h2>
    <p>
      For the SPPB PBD Helper Chrome extension, we use data collected through the extension only
      to provide or improve its disclosed Ez-RPH PBD-to-SPPB sync purpose, including related
      security, support, debugging, reliability, and compliance needs. We do not transfer, sell,
      or use extension data for advertising, credit-worthiness, or unrelated profiling.
    </p>

    <h2>10. Third-Party Services</h2>
    <p>Ez-RPH uses the following third-party services:</p>
    <ul>
      <li>Firebase and Google Cloud (Authentication, Database, Storage, Cloud Functions, Analytics, and hosting)</li>
      <li>Google Identity Toolkit and Secure Token services for Ez-RPH authentication</li>
      <li>Chrome extension APIs when you install and use the SPPB PBD Helper</li>
      <li>Google Play Services / Apple Services</li>
      <li>IDME and SPPB websites when you choose to use those external services with the helper</li>
    </ul>
    <p>These services have their own privacy policies governing data use.</p>

    <h2>11. Offline Data</h2>
    <p>
      Ez-RPH stores lesson plans locally on your device for offline access. 
      This data remains on your device until you choose to sync it to the cloud.
    </p>

    <h2>12. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of any changes 
      by posting the new policy in the application and updating the "Last updated" date.
    </p>

    <h2>13. Contact Us</h2>
    <p>
      If you have any questions about this Privacy Policy or wish to exercise your data rights, 
      please contact us at:
    </p>
    <div className="contact-info">
      <p><strong>Email:</strong> <a href="mailto:support@gataltech.com">support@gataltech.com</a></p>
      <p><strong>Phone:</strong> <a href="tel:+60128756026">+60 12-875 6026</a></p>
      <p><strong>WhatsApp:</strong> <a href="https://wa.me/60128756026">+60 12-875 6026</a></p>
      <p><strong>Company:</strong> Gatal Tech Enterprise, Malaysia</p>
    </div>
  </article>
);

const TermsOfService: React.FC = () => (
  <article className="legal-content">
    <h1>Terms of Service</h1>
    <p className="legal-updated">Last updated: May 2026</p>

    <h2>1. Acceptance of Terms</h2>
    <p>
      By accessing and using Ez-RPH, provided by Gatal Tech Enterprise ("we," "us," or "our"), 
      a business registered in Malaysia, you accept and agree to be bound by these Terms of Service. 
      If you do not agree to these terms, please do not use our application.
    </p>

    <h2>2. Description of Service</h2>
    <p>
      Ez-RPH is a lesson planning application designed to help Malaysian teachers create, manage, 
      and track their Rancangan Pengajaran Harian (RPH) efficiently. The service includes features such as:
    </p>
    <ul>
      <li>Offline lesson planning</li>
      <li>Curriculum integration (KSSR/KSSM)</li>
      <li>School organization management</li>
      <li>Cloud synchronization</li>
      <li>Export to PDF, Excel, and CSV</li>
    </ul>

    <h2>3. User Accounts</h2>
    <p>To use certain features of Ez-RPH, you must create an account. You are responsible for:</p>
    <ul>
      <li>Maintaining the confidentiality of your account credentials</li>
      <li>All activities that occur under your account</li>
      <li>Providing accurate and complete information during registration</li>
      <li>Notifying us immediately of any unauthorized use of your account</li>
    </ul>

    <h2>4. Use License</h2>
    <p>
      We grant you a limited, non-exclusive, non-transferable license to use Ez-RPH for personal 
      and professional educational purposes. Under this license, you may not:
    </p>
    <ul>
      <li>Modify, copy, or create derivative works based on the application</li>
      <li>Use the application for any commercial purpose outside of your teaching duties</li>
      <li>Attempt to decompile, reverse engineer, or extract source code</li>
      <li>Remove any copyright or proprietary notices</li>
      <li>Transfer your license to another person or organization</li>
      <li>Use the application in any way that violates applicable laws</li>
    </ul>

    <h2>5. Subscription and Payments</h2>
    <p>Ez-RPH offers both free and premium subscription plans. For premium subscriptions:</p>
    <ul>
      <li>Payment is processed through Google Play or Apple App Store</li>
      <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
      <li>Refunds are subject to the respective app store policies</li>
      <li>We reserve the right to modify pricing with reasonable notice</li>
    </ul>

    <h2>6. User Content</h2>
    <p>
      You retain ownership of all lesson plans and content you create using Ez-RPH. 
      By using our service, you grant us a limited license to store, process, and display 
      your content solely for the purpose of providing the service.
    </p>

    <h2>7. School Organizations</h2>
    <p>If you join a school organization within Ez-RPH:</p>
    <ul>
      <li>Your lesson plans may be visible to administrators (headmasters/admins) within that organization</li>
      <li>Administrators may approve or reject submitted lesson plans</li>
      <li>You may leave an organization at any time</li>
    </ul>

    <h2>8. Disclaimer of Warranties</h2>
    <p>
      Ez-RPH is provided "as is" without warranties of any kind. We do not guarantee that 
      the service will be uninterrupted, error-free, or meet your specific requirements. 
      We are not responsible for any loss of data due to technical issues.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by Malaysian law, Gatal Tech Enterprise shall not be 
      liable for any indirect, incidental, special, consequential, or punitive damages arising 
      from your use of Ez-RPH.
    </p>

    <h2>10. Changes to Terms</h2>
    <p>
      We may update these Terms of Service from time to time. Continued use of the application 
      after changes constitutes acceptance of the new terms. We will notify users of significant 
      changes through the application.
    </p>

    <h2>11. Governing Law</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of Malaysia. 
      Any disputes shall be subject to the exclusive jurisdiction of the Malaysian courts.
    </p>

    <h2>12. Contact Information</h2>
    <p>If you have any questions about these Terms of Service, please contact us at:</p>
    <div className="contact-info">
      <p><strong>Email:</strong> <a href="mailto:support@gataltech.com">support@gataltech.com</a></p>
      <p><strong>Phone:</strong> <a href="tel:+60128756026">+60 12-875 6026</a></p>
      <p><strong>WhatsApp:</strong> <a href="https://wa.me/60128756026">+60 12-875 6026</a></p>
      <p><strong>Company:</strong> Gatal Tech Enterprise, Malaysia</p>
    </div>

    <h2>13. Student Registry Supplement</h2>
    <p>
      By entering teacher, class, student, or roster data, you warrant that you have lawful authority from yourself,
      your school, or the relevant school administrator to provide and manage that data in Ez-RPH. Student data may
      relate to minors and may include sensitive fields. You must not enter, import, export, merge, or share student
      data unless you have authority to do so.
    </p>

    <h2>14. School-Scoped Storage and Access</h2>
    <p>
      Ez-RPH stores teacher profiles, RPH records, classes, rosters, and student records under the selected school.
      Private student rosters created by a solo teacher remain visible only to that teacher until an explicit share
      or school-import workflow is accepted. School-managed rosters may be accessed by headmasters, school admins,
      assigned teachers, and support staff where required to provide the service. Leaving a school does not
      automatically delete school-scoped data; export, deletion, correction, and DSAR processes apply.
    </p>

    <h2>15. Company Operational Access</h2>
    <p>
      Gatal Tech Enterprise and its processors may access customer and student data only for hosting, support,
      debugging, security, backups, compliance, billing, administration, service improvement, lawful requests,
      abuse prevention, and continuity of the service. We do not sell student data. You are responsible for the
      accuracy of data you enter and for misuse of your account.
    </p>

    <h2>16. Imports, Merges, Exports, and AI</h2>
    <p>
      Student import and merge actions must be reviewed by an authorized school user and must not rely on name-only
      matching. Exports may include personal or sensitive data and require proper authority. Ez-RPH will not use
      student-identifiable data for AI model training unless a separate explicit permission and policy says so.
      These student-data terms require legal review before broad production rollout.
    </p>
  </article>
);

export default Legal;
